/**
 * SourceTablesPanel — Edit the source_tables section of a job.
 *
 * Each row has its own Schema picker (populated from the source connection's
 * real, non-system schemas — mirrors TargetTablePanel's Target Schema
 * dropdown) plus a bare table-name field; the two are joined back into the
 * "schema.table" string the job actually persists (see schemaTable.ts).
 * When a job's source_tables come from an accepted Data Converter / uploaded
 * mapping proposal, each row's table string already carries the mapping's
 * own source schema prefix (see PipelineEngineerService's
 * ``create_proposal_from_data_converter`` / ``import_mapping_proposal``), so
 * splitting it here is all that's needed for the picker to default to that
 * schema — no extra wiring required.
 *
 * Driver radio (exclusive), join_type select and join_on textarea
 * are only shown for non-driver rows. Also hosts the job-level Primary
 * Keys field, rendered inside the driver row's own card (moved here from
 * the New Job creation form — primary keys describe the driver table's
 * natural key, so they belong directly alongside its other driver-only
 * fields like Watermark Column).
 *
 * The driver row's Watermark Column and Primary Keys fields can be
 * auto-detected from the source connection: "Auto-Detect from Source"
 * introspects the driver table's columns (metadata only — names/types,
 * never sample values) and (a) offers likely watermark candidates (date/
 * timestamp-shaped columns) as a dropdown instead of free text, and (b)
 * auto-populates Primary Keys from the table's actual PK metadata when
 * available, or offers a column picklist to compose it manually when not.
 *
 * Save persists source_tables (PATCH) then, only if changed, primary_keys
 * (PUT) — sequentially, using the LAST response for local state, so the
 * job-level `updatedAt`-keyed reset effect can't stomp one field with a
 * stale copy of the other (see ColumnMappingGrid's target-table fix for
 * the same race).
 */

import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, Loader2, Search } from "lucide-react";
import type {
  PEJob,
  SchemaColumn,
  SourceTableSpec,
  SourceTablesPayload,
} from "../../../features/pipeline-engineer/types";
import {
  patchSourceTables,
  updateJob,
  listSourceSchemas,
} from "../../../features/pipeline-engineer/api/pe-client";
import { useIntrospectTask } from "../../../features/pipeline-engineer/useAgenticApi";
import {
  splitSchemaTable,
  joinSchemaTable,
} from "../../../features/pipeline-engineer/schemaTable";
import {
  detectWatermarkCandidates,
  detectPrimaryKeyColumns,
} from "../../../features/pipeline-engineer/watermarkDetect";
import { SchemaSelect } from "./SchemaSelect";

function parseCSV(raw: string): string[] {
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

function toCSV(values: string[]): string {
  return values.join(", ");
}

// ---------------------------------------------------------------------------
// Internal mutable row type
// ---------------------------------------------------------------------------

interface Row {
  id: string; // local key
  schema: string;
  table: string; // bare table name, no schema prefix (schema is its own field)
  alias: string;
  isDriver: boolean;
  joinType: "LEFT" | "INNER" | "RIGHT";
  joinOn: string;
  watermarkCol: string;
}

function specToRow(s: SourceTableSpec, idx: number): Row {
  const { schema, table } = splitSchemaTable(s.table);
  return {
    id: `row-${idx}-${s.table}`,
    schema,
    table,
    alias: s.alias,
    isDriver: s.isDriver,
    joinType: (s.joinType ?? "LEFT") as "LEFT" | "INNER" | "RIGHT",
    joinOn: s.joinOn ?? "",
    watermarkCol: s.watermarkCol ?? "",
  };
}

function rowsToPayload(rows: Row[]): SourceTablesPayload {
  return {
    source_tables: rows.map((r) => ({
      table: joinSchemaTable(r.schema, r.table),
      alias: r.alias,
      is_driver: r.isDriver,
      join_type: r.isDriver ? null : r.joinType,
      join_on: r.isDriver ? null : r.joinOn || null,
      watermark_col: r.isDriver ? r.watermarkCol || null : null,
    })),
  };
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const INPUT_CLASS =
  "w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-white/30 focus:outline-none focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/30";

const LABEL_CLASS = "block text-xs font-medium text-white/60 mb-1";

const JOIN_TYPES = ["LEFT", "INNER", "RIGHT"] as const;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface SourceTablesPanelProps {
  job: PEJob;
  /** Project ID — forwarded to the source-schema introspection task. */
  projectId: string;
  onSaved: (updated: PEJob) => void;
  onDirtyChange: (dirty: boolean) => void;
  onConflict: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SourceTablesPanel({
  job,
  projectId,
  onSaved,
  onDirtyChange,
  onConflict,
}: SourceTablesPanelProps) {
  const [rows, setRows] = useState<Row[]>(() =>
    job.sourceTables.map(specToRow),
  );
  const [primaryKeysRaw, setPrimaryKeysRaw] = useState(toCSV(job.primaryKeys));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // ── Schema picker (shared list across all rows — one source connection) ──
  const [schemas, setSchemas] = useState<string[]>([]);
  const [loadingSchemas, setLoadingSchemas] = useState(false);
  const [schemaError, setSchemaError] = useState<string | null>(null);
  const [schemaManualEntry, setSchemaManualEntry] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoadingSchemas(true);
    setSchemaError(null);
    listSourceSchemas(job.pipelineId)
      .then((list) => {
        if (cancelled) return;
        setSchemas(list);
        const initialSchemas = job.sourceTables.map(
          (s) => splitSchemaTable(s.table).schema,
        );
        setSchemaManualEntry(
          list.length === 0 ||
            initialSchemas.some((s) => s !== "" && !list.includes(s)),
        );
      })
      .catch((err) => {
        if (cancelled) return;
        setSchemaError(
          err instanceof Error ? err.message : "Failed to load schemas.",
        );
        setSchemaManualEntry(true);
      })
      .finally(() => {
        if (!cancelled) setLoadingSchemas(false);
      });
    return () => {
      cancelled = true;
    };
    // Only re-fetch when the pipeline's source connection could have changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.pipelineId]);

  // ── Watermark / Primary Key auto-detect (driver row only) ────────────────
  const sourceIntrospect = useIntrospectTask(
    job.pipelineId,
    projectId,
    "source",
  );
  const [detectedColumns, setDetectedColumns] = useState<SchemaColumn[] | null>(
    null,
  );
  const [waitingForDetect, setWaitingForDetect] = useState(false);
  const [watermarkManualEntry, setWatermarkManualEntry] = useState(false);

  const driverRow = rows.find((r) => r.isDriver) ?? null;

  // ── Browse Existing Tables (per row, scoped to that row's schema) ───────
  // Separate hook instance from `sourceIntrospect` above — that one is
  // dedicated to the driver row's own watermark/PK column detection and
  // must not be overwritten by a different row's "browse tables" request.
  const [tableBrowseRowId, setTableBrowseRowId] = useState<string | null>(null);
  const tableBrowse = useIntrospectTask(job.pipelineId, projectId, "source");
  const browseTableNames =
    tableBrowse.task?.result?.tables.map((t) => t.name) ?? [];

  const handleBrowseTables = useCallback(
    (row: Row) => {
      setTableBrowseRowId(row.id);
      tableBrowse.reset();
      void tableBrowse.start(null, row.schema || null);
    },
    [tableBrowse],
  );

  const handleAutoDetect = useCallback(() => {
    if (driverRow === null || driverRow.table.trim() === "") return;
    setDetectedColumns(null);
    setWaitingForDetect(true);
    void sourceIntrospect.start([driverRow.table], driverRow.schema || null);
  }, [driverRow, sourceIntrospect]);

  // Fires once the driver-table introspection reaches a terminal status.
  useEffect(() => {
    if (!waitingForDetect) return;
    const status = sourceIntrospect.task?.status;
    if (status === "completed") {
      setWaitingForDetect(false);
      const columns = sourceIntrospect.task?.result?.tables[0]?.columns ?? [];
      setDetectedColumns(columns);

      const candidates = detectWatermarkCandidates(columns);
      if (candidates.length > 0) {
        setRows((prev) =>
          prev.map((r) =>
            r.isDriver && r.watermarkCol === ""
              ? { ...r, watermarkCol: candidates[0].name }
              : r,
          ),
        );
      }

      const pkCols = detectPrimaryKeyColumns(columns);
      if (pkCols.length > 0) {
        setPrimaryKeysRaw((prev) => (prev === "" ? toCSV(pkCols) : prev));
      }
    } else if (status === "failed") {
      setWaitingForDetect(false);
    }
  }, [waitingForDetect, sourceIntrospect.task]);

  // Clear stale detection results whenever the driver row's table/schema changes.
  const driverTableKey = driverRow
    ? `${driverRow.schema}.${driverRow.table}`
    : "";
  useEffect(() => {
    setDetectedColumns(null);
    sourceIntrospect.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driverTableKey]);

  // Sync dirty state to parent.
  // Compare rows (sans local `id`) against what specToRow parses from job — both
  // use the Row shape (camelCase), avoiding a camelCase-vs-snake_case mismatch
  // that would make isDirty permanently true.
  useEffect(() => {
    const pick = (r: Row) => ({
      schema: r.schema,
      table: r.table,
      alias: r.alias,
      isDriver: r.isDriver,
      joinType: r.joinType,
      joinOn: r.joinOn,
      watermarkCol: r.watermarkCol,
    });
    const original = JSON.stringify(job.sourceTables.map(specToRow).map(pick));
    const current = JSON.stringify(rows.map(pick));
    const pkChanged = primaryKeysRaw !== toCSV(job.primaryKeys);
    onDirtyChange(original !== current || pkChanged);
  }, [rows, primaryKeysRaw, job.sourceTables, job.primaryKeys, onDirtyChange]);

  // Reset rows when job prop changes (e.g. after save)
  useEffect(() => {
    setRows(job.sourceTables.map(specToRow));
    setPrimaryKeysRaw(toCSV(job.primaryKeys));
  }, [job.id, job.updatedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  const setDriver = useCallback((id: string) => {
    setRows((prev) => prev.map((r) => ({ ...r, isDriver: r.id === id })));
  }, []);

  const updateRow = useCallback((id: string, patch: Partial<Row>) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }, []);

  const removeRow = useCallback((id: string) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
  }, []);

  const addRow = useCallback(() => {
    const newRow: Row = {
      id: `row-new-${Date.now()}`,
      schema: "",
      table: "",
      alias: "",
      isDriver: rows.length === 0,
      joinType: "LEFT",
      joinOn: "",
      watermarkCol: "",
    };
    setRows((prev) => [...prev, newRow]);
  }, [rows.length]);

  const handleSave = useCallback(async () => {
    setSaveError(null);
    setSaving(true);
    try {
      let updated = await patchSourceTables(
        job.pipelineId,
        job.id,
        rowsToPayload(rows),
      );
      if (primaryKeysRaw !== toCSV(job.primaryKeys)) {
        updated = await updateJob(job.pipelineId, job.id, {
          primary_keys: parseCSV(primaryKeysRaw),
        });
      }
      setRows(updated.sourceTables.map(specToRow));
      setPrimaryKeysRaw(toCSV(updated.primaryKeys));
      onSaved(updated);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Save failed.";
      if (msg.includes("409") || msg.toLowerCase().includes("conflict")) {
        onConflict();
      } else {
        setSaveError(msg);
      }
    } finally {
      setSaving(false);
    }
  }, [
    rows,
    primaryKeysRaw,
    job.pipelineId,
    job.id,
    job.primaryKeys,
    onSaved,
    onConflict,
  ]);

  const detectedPkColumns =
    detectedColumns !== null ? detectPrimaryKeyColumns(detectedColumns) : [];
  const watermarkCandidates =
    detectedColumns !== null ? detectWatermarkCandidates(detectedColumns) : [];
  const candidateNames = new Set(watermarkCandidates.map((c) => c.name));
  const watermarkOptions =
    detectedColumns !== null
      ? [
          ...watermarkCandidates,
          ...detectedColumns.filter((c) => !candidateNames.has(c.name)),
        ]
      : null;

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        {rows.map((row, idx) => (
          <div
            key={row.id}
            className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-3"
          >
            {/* Row header: driver radio + index */}
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name={`driver-${job.id}`}
                  checked={row.isDriver}
                  onChange={() => setDriver(row.id)}
                  className="accent-brand-500"
                  aria-label={`Set row ${idx + 1} as driver table`}
                />
                <span className="text-xs text-white/60">Driver</span>
              </label>
              <span className="text-xs text-white/30">Table {idx + 1}</span>
              <div className="ml-auto">
                <button
                  type="button"
                  aria-label={`Remove table row ${idx + 1}`}
                  onClick={() => removeRow(row.id)}
                  className="p-1.5 text-red-400/60 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                >
                  <Trash2 size={13} aria-hidden="true" />
                </button>
              </div>
            </div>

            {/* Schema + Table + alias */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label htmlFor={`st-schema-${row.id}`} className={LABEL_CLASS}>
                  Schema
                </label>
                <SchemaSelect
                  id={`st-schema-${row.id}`}
                  value={row.schema}
                  schemas={schemas}
                  loading={loadingSchemas}
                  error={schemaError}
                  manualEntry={schemaManualEntry}
                  onManualEntryToggle={() =>
                    setSchemaManualEntry((prev) => !prev)
                  }
                  onChange={(value) => {
                    updateRow(row.id, { schema: value });
                    // Re-scope "Browse Tables" to whatever schema is picked next.
                    if (tableBrowseRowId === row.id) {
                      tableBrowse.reset();
                      setTableBrowseRowId(null);
                    }
                  }}
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label htmlFor={`st-table-${row.id}`} className={LABEL_CLASS}>
                    Table
                  </label>
                  <button
                    type="button"
                    onClick={() => handleBrowseTables(row)}
                    disabled={
                      tableBrowse.loading && tableBrowseRowId === row.id
                    }
                    className="flex items-center gap-1 text-[10px] text-brand-400 hover:text-brand-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {tableBrowse.loading && tableBrowseRowId === row.id ? (
                      <Loader2
                        size={11}
                        className="animate-spin"
                        aria-hidden="true"
                      />
                    ) : (
                      <Search size={11} aria-hidden="true" />
                    )}
                    Browse Tables
                  </button>
                </div>
                <input
                  id={`st-table-${row.id}`}
                  type="text"
                  className={INPUT_CLASS}
                  placeholder="table_name"
                  value={row.table}
                  onChange={(e) => updateRow(row.id, { table: e.target.value })}
                />
                {tableBrowseRowId === row.id &&
                  tableBrowse.task?.status === "completed" && (
                    <select
                      aria-label={`Select an existing table for row ${idx + 1}`}
                      className="w-full mt-1 bg-black/30 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white/80"
                      value=""
                      onChange={(e) => {
                        if (e.target.value)
                          updateRow(row.id, { table: e.target.value });
                      }}
                    >
                      <option value="">
                        {browseTableNames.length === 0
                          ? "— No existing tables found in this schema —"
                          : "— Select a table to fill the field above —"}
                      </option>
                      {browseTableNames.map((name) => (
                        <option key={name} value={name}>
                          {name}
                        </option>
                      ))}
                    </select>
                  )}
                {tableBrowseRowId === row.id && tableBrowse.error !== null && (
                  <p className="text-red-400 text-xs mt-1" role="alert">
                    {tableBrowse.error}
                  </p>
                )}
              </div>
              <div>
                <label htmlFor={`st-alias-${row.id}`} className={LABEL_CLASS}>
                  Alias
                </label>
                <input
                  id={`st-alias-${row.id}`}
                  type="text"
                  className={INPUT_CLASS}
                  placeholder="e.g. o"
                  value={row.alias}
                  onChange={(e) => updateRow(row.id, { alias: e.target.value })}
                />
              </div>
            </div>

            {/* Driver: watermark column + primary keys; Non-driver: join type + join on */}
            {row.isDriver ? (
              <>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label
                      htmlFor={`st-wm-${row.id}`}
                      className="text-xs font-medium text-white/60"
                    >
                      Watermark Column (for incremental / SCD)
                    </label>
                    <button
                      type="button"
                      onClick={handleAutoDetect}
                      disabled={waitingForDetect || row.table.trim() === ""}
                      className="flex items-center gap-1 text-[10px] text-brand-400 hover:text-brand-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {waitingForDetect ? (
                        <Loader2
                          size={11}
                          className="animate-spin"
                          aria-hidden="true"
                        />
                      ) : (
                        <Search size={11} aria-hidden="true" />
                      )}
                      Auto-Detect from Source
                    </button>
                  </div>
                  {watermarkOptions !== null && !watermarkManualEntry ? (
                    <select
                      id={`st-wm-${row.id}`}
                      className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                      value={row.watermarkCol}
                      onChange={(e) =>
                        updateRow(row.id, { watermarkCol: e.target.value })
                      }
                    >
                      <option value="">— None —</option>
                      {watermarkOptions.map((c) => (
                        <option key={c.name} value={c.name}>
                          {c.name} ({c.type})
                          {candidateNames.has(c.name)
                            ? " — likely watermark"
                            : ""}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      id={`st-wm-${row.id}`}
                      type="text"
                      className={INPUT_CLASS}
                      placeholder="e.g. updated_at"
                      value={row.watermarkCol}
                      onChange={(e) =>
                        updateRow(row.id, { watermarkCol: e.target.value })
                      }
                    />
                  )}
                  {watermarkOptions !== null && (
                    <button
                      type="button"
                      onClick={() => setWatermarkManualEntry((prev) => !prev)}
                      className="text-[10px] text-white/40 hover:text-white/60 underline mt-1"
                    >
                      {watermarkManualEntry
                        ? "Choose from detected columns instead"
                        : "Enter column manually instead"}
                    </button>
                  )}
                  {sourceIntrospect.error !== null && (
                    <p className="text-red-400 text-xs mt-1" role="alert">
                      {sourceIntrospect.error}
                    </p>
                  )}
                </div>
                <div>
                  <label
                    htmlFor={`st-primary-keys-${row.id}`}
                    className={LABEL_CLASS}
                  >
                    Primary Keys (comma-separated)
                  </label>
                  <input
                    id={`st-primary-keys-${row.id}`}
                    type="text"
                    className={`${INPUT_CLASS} font-mono`}
                    placeholder="e.g. id, order_id"
                    value={primaryKeysRaw}
                    onChange={(e) => setPrimaryKeysRaw(e.target.value)}
                  />
                  <p className="text-xs text-white/30 mt-1">
                    Comma-separated column names used to match source rows to
                    target rows (required for incremental merge / SCD
                    strategies). Auto-populated from source primary-key metadata
                    when &quot;Auto-Detect from Source&quot; finds one.
                  </p>
                  {detectedColumns !== null &&
                    detectedPkColumns.length === 0 && (
                      <div className="mt-2">
                        <label
                          htmlFor={`st-pk-list-${row.id}`}
                          className="block text-[10px] text-white/40 mb-1"
                        >
                          No primary key metadata found on this table — pick
                          column(s) to compose the list above
                        </label>
                        <select
                          id={`st-pk-list-${row.id}`}
                          multiple
                          className="w-full bg-black/30 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white/80"
                          value={parseCSV(primaryKeysRaw)}
                          onChange={(e) => {
                            const selected = Array.from(
                              e.target.selectedOptions,
                            ).map((o) => o.value);
                            setPrimaryKeysRaw(toCSV(selected));
                          }}
                        >
                          {detectedColumns.map((c) => (
                            <option key={c.name} value={c.name}>
                              {c.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                </div>
              </>
            ) : (
              <>
                <div>
                  <label htmlFor={`st-jt-${row.id}`} className={LABEL_CLASS}>
                    Join Type
                  </label>
                  <select
                    id={`st-jt-${row.id}`}
                    className={INPUT_CLASS}
                    value={row.joinType}
                    onChange={(e) =>
                      updateRow(row.id, {
                        joinType: e.target.value as "LEFT" | "INNER" | "RIGHT",
                      })
                    }
                  >
                    {JOIN_TYPES.map((jt) => (
                      <option key={jt} value={jt}>
                        {jt} JOIN
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor={`st-jo-${row.id}`} className={LABEL_CLASS}>
                    Join ON Condition
                  </label>
                  <textarea
                    id={`st-jo-${row.id}`}
                    rows={2}
                    className={`${INPUT_CLASS} resize-none font-mono text-xs`}
                    placeholder="e.g. o.customer_id = c.id"
                    value={row.joinOn}
                    onChange={(e) =>
                      updateRow(row.id, { joinOn: e.target.value })
                    }
                  />
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={addRow}
        className="flex items-center gap-1.5 px-3 py-2 bg-white/5 hover:bg-white/10 text-white/70 border border-white/10 rounded-lg text-xs font-medium transition-colors w-full justify-center"
      >
        <Plus size={13} aria-hidden="true" />
        Add Table
      </button>

      {saveError !== null && (
        <p className="text-red-400 text-xs" role="alert">
          {saveError}
        </p>
      )}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={saving}
          className="flex items-center gap-1.5 px-4 py-2 bg-brand-500 hover:bg-brand-600 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving && (
            <Loader2 size={12} className="animate-spin" aria-hidden="true" />
          )}
          Save Source Tables &amp; Keys
        </button>
      </div>
    </div>
  );
}
